// TODO: should be provided from backend
import _ from 'lodash'
import constants from './backendStringConstants'
import getRoundedScore from './getRoundedScore'

const {
  COVID,
  NODULE,
  TB,
  TB_FEEDBACK,
  NODULE_FEEDBACK,
  ABNORMAL_FEEDBACK,
  ABNORMAL,
  COVID_FEEDBACK,
  XRAY_PENDING,
  INVALID_XRAY,
  PROCESSING,
  PROCESSED,
  NAME,
  AI_STATES,
  AI_TB_FEEDBACK,
  AI_COVID_FEEDBACK,
  RADIOLOGIST_TB_FEEDBACK,
  RADIOLOGIST_COVID_FEEDBACK,
  RADIOLOGIST_NODULE_FEEDBACK,
  RADIOLOGIST_STATES_TB,
  RADIOLOGIST_STATES_COVID,
  RADIOLOGIST_STATES_NODULE,
  NONAME_NA,
  PRESUMPTIVE,
  NEGATIVE,
  TO_BE_REVIEWED,
  YES,
  NO,
  INDICATIVE,
  NORMAL,
  TBR,
  MALIGNANCY_RISK,
  MALIGNANCY_RISK_KEY,
  MALIGNANCY_FEEDBACK,
  AI_NODULE_FEEDBACK
} = constants

function truncateMiddle(fullStr, strLen, separatorParam) {
  if (_.isEmpty(fullStr) || fullStr.length <= strLen) {
    return fullStr
  }

  const separator = separatorParam || '...'

  const sepLen = separator.length
  const charsToShow = strLen - sepLen
  const frontChars = Math.ceil(charsToShow / 2)
  const backChars = Math.floor(charsToShow / 2)

  return (
    fullStr.substr(0, frontChars) +
    separator +
    fullStr.substr(fullStr.length - backChars)
  )
}

function clientTableAccessors(accessor, value) {
  switch (accessor) {
    case TB_FEEDBACK:
      if (value === -1) return NEGATIVE
      if (value === 1) return PRESUMPTIVE
      if (value === 2) return TO_BE_REVIEWED
      break

    case NODULE_FEEDBACK:
      if (_.lowerCase(value) === YES) return INDICATIVE
      if (_.lowerCase(value) === NO) return NEGATIVE
      break
    case ABNORMAL_FEEDBACK:
      if (_.lowerCase(value) === YES) return ABNORMAL
      if (_.lowerCase(value) === TBR) return TO_BE_REVIEWED
      if (_.lowerCase(value) === NO) return NORMAL
      break

    default:
      return 'Feedback Not Available'
  }
  return null
}

const hasInvalidState = state => {
  // all the stats for cxr_state => invalid_xray, xray_pending, processing, processed
  return (
    [XRAY_PENDING, PROCESSING, INVALID_XRAY].indexOf(_.toLower(state)) !== -1
  )
}

const hasXrayUploaded = state => {
  return [PROCESSED, PROCESSING, INVALID_XRAY].indexOf(_.toLower(state)) !== -1
}

const buildFeedbacks = (useCases, datum, featureToggles) => {
  const tbFeedback = _.get(datum, [TB_FEEDBACK])
  const noduleFeedback = _.get(datum, [NODULE_FEEDBACK])
  const malignancyFeedback = _.get(datum, [MALIGNANCY_FEEDBACK])
  const abnormalFeedback = _.get(datum, [ABNORMAL_FEEDBACK])
  const finalUseCases = [...useCases]
  const feedbacks = finalUseCases.map(name => {
    let feedback = {}
    if (name === TB) {
      const value = clientTableAccessors(TB_FEEDBACK, tbFeedback)
      const tbScore = getRoundedScore(_.get(datum, ['tbScore']), featureToggles)
      feedback = {
        value,
        suffix: tbScore,
        valueLabel: value,
        label: 'TB',
        usecase: TB
      }
    }

    if (name === COVID) {
      const value = _.get(datum, [COVID_FEEDBACK])
      feedback = {
        value,
        valueLabel: value,
        label: `COVID-19 Risk`,
        usecase: COVID
      }
    }
    if (name === NODULE) {
      const value = clientTableAccessors(NODULE_FEEDBACK, noduleFeedback)
      feedback = {
        value,
        valueLabel: value,
        label: 'Nodule',
        usecase: 'nodule'
      }
    }
    if (name === ABNORMAL) {
      const value = clientTableAccessors(ABNORMAL_FEEDBACK, abnormalFeedback)
      feedback = {
        value,
        valueLabel: value,
        label: 'CXR',
        usecase: 'abnormal'
      }
    }
    return feedback
  })

  feedbacks.push({
    value: malignancyFeedback,
    valueLabel: malignancyFeedback,
    label: MALIGNANCY_RISK,
    usecase: MALIGNANCY_RISK_KEY
  })

  const scanType = _.get(datum, 'scanType', 'cxr')
  const isMsk = scanType === constants.MSK

  if (isMsk) {
    const mskFeedback = _.get(datum, 'abnormal', '')
    const value = mskFeedback === 1 ? constants.ABNORMAL : constants.NEGATIVE
    return {
      feedbacks: [
        {
          value,
          valueLabel: value,
          label: 'Msk',
          usecase: constants.ABNORMAL
        }
      ]
    }
  }

  return { feedbacks, tbFeedback }
}

const buildImpressions = (useCases, datum, featureToggles) => {
  const aiTbFeedback = _.get(datum, [AI_TB_FEEDBACK])
  const aiCovidFeedback = _.get(datum, [AI_COVID_FEEDBACK])
  const aiNoduleFeedback = _.get(datum, [AI_NODULE_FEEDBACK])
  const impressions = []
  if (useCases && useCases.indexOf(TB) !== -1) {
    const value = AI_STATES[aiTbFeedback]
    const tbScore = getRoundedScore(_.get(datum, ['tbScore']), featureToggles)
    const aiTbImpression = {
      value,
      valueLabel: value,
      label: `AI-TB`,
      suffix: tbScore,
      usecase: TB
    }
    impressions.push(aiTbImpression)
  }

  if (useCases && useCases.indexOf(COVID) !== -1) {
    const aiCovidImpression = {
      value: aiCovidFeedback,
      valueLabel: aiCovidFeedback,
      label: `AI-COVID`,
      usecase: COVID
    }
    impressions.push(aiCovidImpression)
  }

  if (useCases && useCases.indexOf(NODULE) !== -1) {
    const value = AI_STATES[aiNoduleFeedback]
    const aiNoduleImpression = {
      value,
      valueLabel: value,
      label: 'AI-NODULE',
      usecase: NODULE
    }
    impressions.push(aiNoduleImpression)
  }

  if (useCases && useCases.indexOf(TB) !== -1) {
    const radiologistTbFeedback = _.get(datum, [RADIOLOGIST_TB_FEEDBACK])
    const value = RADIOLOGIST_STATES_TB[radiologistTbFeedback]
    const radiologistImpression = {
      value,
      valueLabel: value,
      label: `Radiologist-TB`,
      usecase: TB
    }
    impressions.push(radiologistImpression)
  }

  if (useCases && useCases.indexOf(COVID) !== -1) {
    const radCovidFeedback = _.get(datum, [RADIOLOGIST_COVID_FEEDBACK])
    const value = RADIOLOGIST_STATES_COVID[radCovidFeedback]
    const radCovidImpression = {
      value,
      valueLabel: value,
      label: `Radiologist-COVID-19`,
      usecase: COVID
    }
    impressions.push(radCovidImpression)
  }

  if (useCases && useCases.indexOf(NODULE) !== -1) {
    const radNoduleFeedback = _.get(datum, [RADIOLOGIST_NODULE_FEEDBACK])
    const value = RADIOLOGIST_STATES_NODULE[radNoduleFeedback]
    const radNoduleImpression = {
      value,
      valueLabel: value,
      label: 'Radiologist-NODULE',
      usecase: NODULE
    }
    impressions.push(radNoduleImpression)
  }

  return impressions
}

const buildInvalidStateImpressions = datum => {
  const impressions = []

  switch (datum) {
    case XRAY_PENDING:
      impressions.push({
        value: 'Pending',
        valueLabel: 'Pending',
        label: 'Xray'
      })
      break
    case INVALID_XRAY:
      impressions.push({
        value: 'Invalid',
        valueLabel: 'Invalid',
        label: 'Xray'
      })
      break
    case PROCESSING:
      impressions.push({
        value: 'Processing',
        valueLabel: 'Processing',
        label: 'Xray'
      })
      break
    default:
      return null
  }
  return impressions
}

const buildTestConfirmationResults = (datum, tests, testsKeys = []) => {
  const availableTests = _.get(datum, ['tests'], {})

  return testsKeys.map(tKey => {
    const selectedTest = _.find(tests, t => t.key === tKey)
    const { key, name } = selectedTest
    const value = _.get(availableTests, [key, 'details', 'result'], '0')
    const selectedTestConfig = _.find(tests, { key })

    const valueLabel = _.get(selectedTestConfig, ['choicesObj', value])
    return {
      key,
      usecase: key,
      value,
      label: name,
      valueLabel
    }
  })
}

const getResponseDataForClients = list => {
  const site = _.get(list, ['site'])

  return {
    date: _.get(list, ['date']),
    name: _.get(list, ['fullname']),
    Id: _.get(list, ['id']),
    clientId: _.get(list, ['patientID']),
    accessionNumber: _.get(list, ['metadata', 'AccessionNumber'], ''),
    nationalId: _.get(list, ['nationalID']),
    visitId: _.get(list, ['latest_visit_id']),
    visitIdentifier: _.get(list, ['latest_visit_id']),
    visitCount: _.get(list, ['visits_count']),
    site,
    state: _.get(list, ['cxr_state']),
    syncStatus: _.get(list, ['synced']),
    hasXrayUploaded: _.get(list, ['xray_uploaded']),
    scanType: _.get(list, ['scan_type'], 'cxr'),
    abnormal: _.get(list, ['abnormal']),
    report: _.get(list, ['report']),
    age: _.get(list, ['age']),
    gender: _.get(list, ['gender']),
    mppsProgress: _.get(list, ['additional', 'mpps_progress']),
    pediatricFlag: _.get(list, ['pediatric_flag'], false)
  }
}

const getResponseDataForVisits = list => {
  const site = _.get(list, ['patient', 'site'])

  return {
    date: _.get(list, ['date']),
    name: _.get(list, ['patient', 'fullname']),
    Id: _.get(list, ['patient', 'id']),
    clientId: _.get(list, ['patient', 'patientID']),
    accessionNumber: _.get(list, ['metadata', 'AccessionNumber'], ''),
    gender: _.get(list, ['patient', 'gender']),
    age: _.get(list, ['patient', 'age']),
    visitId: _.get(list, ['followupid']), // TODO
    visitIdentifier: _.get(list, ['id']),
    site,
    state: _.get(list, ['cxr_state']),
    hasXrayUploaded: _.get(list, ['xray_uploaded']),
    symptoms: _.get(list, ['followup_symptoms']),
    lastEditedAt: _.get(list, ['last_updated_at']),
    lastEditedBy: _.get(list, ['last_updated_by']),
    tests: _.get(list, ['tests']),
    visitDate: _.get(list, ['date']),
    nva: _.get(list, 'nva'),
    scanType: _.get(list, ['scan_type'], 'cxr'),
    abnormal: _.get(list, ['abnormal']),
    report: _.get(list, 'report'),
    tbScore: _.get(
      list,
      ['ai_feedback', 'tag_results', 'tuberculosis', 'score'],
      ''
    )
  }
}

function pluralizeString(t, value, word, suffix = 's') {
  if (value === 1 || value === 0) {
    return `${value} ${t(`${word}`)}`
  }
  return `${value} ${t(`${word}${suffix}`)}`
}

function checkEmptyClientName(clientName) {
  if (clientName === NONAME_NA || clientName === '' || clientName === null) {
    return true
  }
  return false
}

function getUserRoles(userProfile) {
  return _.get(userProfile, ['user', 'roles'], [])
}

function checkUserType(userProfile, type) {
  const userRole = getUserRoles(userProfile)
  return userRole.indexOf(type) !== -1
}

function isAdminUser(userProfile) {
  return checkUserType(userProfile, constants.ADMIN)
}

function isHealthWorkerUser(userProfile) {
  return checkUserType(userProfile, constants.HEALTHWORKER)
}

function isRadiologistUser(userProfile) {
  return checkUserType(userProfile, constants.RADIOLOGIST)
}

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

function translateClientName(key, userProfile, t) {
  let value = key
  if (userProfile && t) {
    const clientName = _.get(userProfile, ['client_name'], 'client')
    const keyName = key
      .replace('client', clientName.toLowerCase())
      .replace('Client', capitalize(clientName))
    value = t(keyName)
  }
  return value
}

const getTitleSubtitle = (primaryIdentity, name, clientId, userProfile, t) => {
  const isPrimaryInfoName = primaryIdentity === NAME
  const title = isPrimaryInfoName ? name : clientId
  const subTitle = isPrimaryInfoName ? clientId : name
  let label = null
  if (isPrimaryInfoName) {
    label = translateClientName('Client ID', userProfile, t)
  }

  return { title, subTitle, label, isPrimaryInfoName }
}

function getAllRoles(userProfile) {
  const hasRadiologistRole = isRadiologistUser(userProfile)
  const hasHealthWorkerRole = isHealthWorkerUser(userProfile)
  const hasAdminRole = isAdminUser(userProfile)
  return {
    hasRadiologistRole,
    hasHealthWorkerRole,
    hasAdminRole,
    hasProgramManagerRole:
      hasHealthWorkerRole && hasAdminRole && hasRadiologistRole
  }
}

function hasValidRoles(userProfile) {
  const userRoles = getUserRoles(userProfile)
  return (
    !_.isEmpty(userRoles) &&
    _.size(userRoles) <= 3 &&
    (isRadiologistUser(userProfile) ||
      isHealthWorkerUser(userProfile) ||
      isAdminUser(userProfile))
  )
}

function getConvertedFieldOptions(options, t) {
  return options?.map(fieldOptions => {
    let value
    let label
    if (typeof fieldOptions === 'string') {
      value = fieldOptions
      label = t ? t(fieldOptions) : fieldOptions
    } else {
      value = _.get(fieldOptions, ['value'])
      label = t
        ? t(_.get(fieldOptions, ['label']))
        : _.get(fieldOptions, ['label'])
    }

    return {
      label,
      value
    }
  })
}

const getRiskGroupsOptions = (riskGroups, langCode) => {
  const riskGroupSelector = ['additional', langCode]
  return riskGroups.map(option => ({
    label: _.get(option, riskGroupSelector),
    value: _.get(option, ['riskgroupid'])
  }))
}

const getSymptomsOptions = (symptoms, langCode) => {
  const symptomSelector = ['additional', langCode]
  return symptoms.map(option => ({
    label: _.get(option, symptomSelector),
    value: _.get(option, ['symptomid'])
  }))
}

const findSymptomsOrRiskgroupOptions = (
  optionsArray,
  idOptionsArray,
  identifier
) => {
  const filteredOptions = optionsArray
    .map(options => {
      if (idOptionsArray.indexOf(_.get(options, [identifier])) !== -1) {
        return _.get(options, [identifier])
      }
      return null
    })
    .filter(value => value != null)
  return filteredOptions
}

function validateJSON(jsonSchema, obj) {
  // TODO not a generic Valid JSON validator , does not do deep validations

  function compareKeys(a, b) {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    return keysA.length === keysB.length
  }

  let isValid = true
  if (Array.isArray(obj)) {
    if (_.isEmpty(obj)) {
      isValid = false
    } else {
      obj.forEach(item => {
        if (_.isEmpty(item) && typeof item !== 'object') {
          isValid = false
        } else if (!compareKeys(_.get(jsonSchema, [0]), item)) {
          isValid = false
        } else {
          Object.keys(item).forEach(key2 => {
            const jsonSchemaObjVal = _.get(jsonSchema, [0, [key2]], null)
            const itemObjVal = _.get(item, [key2], null)
            /* eslint-disable */
            if (jsonSchemaObjVal !== typeof itemObjVal) {
              isValid = false
            }
            /* eslint-enable */
          })
        }
      })
    }
  } else {
    isValid = false
  }

  return isValid
}

export default {
  clientTableAccessors,
  buildFeedbacks,
  hasInvalidState,
  hasXrayUploaded,
  getTitleSubtitle,
  buildImpressions,
  buildInvalidStateImpressions,
  buildTestConfirmationResults,
  getResponseDataForClients,
  getResponseDataForVisits,
  pluralizeString,
  checkEmptyClientName,
  isRadiologistUser,
  isAdminUser,
  isHealthWorkerUser,
  translateClientName,
  hasValidRoles,
  getUserRoles,
  getAllRoles,
  getConvertedFieldOptions,
  getRiskGroupsOptions,
  getSymptomsOptions,
  findSymptomsOrRiskgroupOptions,
  truncateMiddle,
  validateJSON
}
